import { useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES, IDimensionalityOption } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce, isEqual } from 'lodash';
import { IProductionRequestData, useGetProductionCalculations } from 'services/calculations/production/';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { useGlobalProducts } from 'components/BusinessToolForm/hooks/useGlobalProducts';
import { GLOBAL_UNIT_NAME } from 'components/BusinessToolForm/Sections/Global/form';
import { IGlobalProductEntity } from 'components/BusinessToolForm/Sections/Global/general/products';

import ToastService from 'helpers/ToastService';

import { getProductsProductionData, getProductsProductionParams } from './helper';
import { IFormContentTypes, observedFields } from '../../utils/form';

const PRODUCTION_CALCULATION_DELAY = 200;

export const useProductionCalculation = () => {
  const { id: businessPlanId = '' } = useParams();
  const productsData = useRef<[IGlobalProductEntity[], string]>([[], '']);
  const { setValue, getValues } = useFormContext();
  const isFirstRender = useRef(true);

  const getProductionCalculation = useGetProductionCalculations();

  const { products } = useGlobalProducts();
  const unit = getValues(GLOBAL_UNIT_NAME)

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  if (isFirstRender.current) {
    const production = getProductsProductionData(products)
    productsData.current = [production, unit];
    isFirstRender.current = false;
  }

  const [productionFiledName] = observedFields.production.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (productionData: IProductionRequestData[], unit: IDimensionalityOption) => {
        const params = {
          unit,
          businessPlanId,
          productionRequestData: {
            productionData,
          },
        };

        try {
          const { productionData: calculatedProductionData } = await getProductionCalculation.mutateAsync(params);

          const extractedProductsData = calculatedProductionData.map((data) => data.productionData);

          setValue(productionFiledName, extractedProductsData);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, PRODUCTION_CALCULATION_DELAY),
     
    [],
  );

  const productionCalculationHandler = useCallback(
    async (content: IFormContentTypes['production']) => {
      if (!canAccess) return;

      const [products, operationUnit] = content;
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;
      const extractedProductsData = getProductsProductionData(products);

      if (isEqual(productsData.current, [extractedProductsData, unit])) return;

      productsData.current = [extractedProductsData, unit];

      const productionData = getProductsProductionParams(products);

      debouncedUpdateFunction(productionData, unit);
    },
     
    [debouncedUpdateFunction],
  );

  return productionCalculationHandler;
};
